import type { ProductBadge } from "types/product";

import type { ProductCommonFieldsFragment } from "types/storefront.generated";

/**
 * Resolve badge from product tags.
 * @example badge:dark:Sale:au
 */
export class ProductBadgeResolver {
  /**
   * Is sale enabled for the current customer?
   * This prompts the resolver to select the sale badge if found.
   */
  protected enableSale: boolean;

  /**
   * Country code for current site.
   * This selects the badge tag appropriate for the country.
   */
  protected countryCode: string;

  /**
   * Product from Shopify.
   */
  protected shopifyProduct: Pick<
    ProductCommonFieldsFragment,
    "statusMetafield" | "availableForSale" | "tags"
  >;

  constructor({
    enableSale,
    countryCode,
    shopifyProduct,
  }: {
    enableSale: ProductBadgeResolver["enableSale"];
    countryCode: ProductBadgeResolver["countryCode"];
    shopifyProduct: ProductBadgeResolver["shopifyProduct"];
  }) {
    this.enableSale = enableSale;
    this.countryCode = countryCode;
    this.shopifyProduct = shopifyProduct;
  }

  resolve(): ProductBadge | null {
    // Resolve product status from metafield.
    const productStatus = this.shopifyProduct.statusMetafield?.value ?? null;

    // If product is unavailable, but that status is set to Seasonal or Core, return Notify Me, else return Out Of Stock.
    if (!this.shopifyProduct.availableForSale && productStatus) {
      return ["Seasonal", "Core"].includes(productStatus)
        ? { children: "Notify Me", variant: "secondary" }
        : { children: "Out Of Stock", variant: "primary" };
    }

    // Keep track of the badge.
    let badge: ProductBadge | null = null;

    // Loop over each tag to find a badge.
    for (const tag of this.shopifyProduct.tags) {
      // Tag does not start with "badge", throw it away.
      if (!tag.startsWith("badge:")) continue;

      // Split the tag into it's parts.
      const tagParts = tag.split(":");
      let [_prefix, colour, label, countryCode, saleType] = tagParts;

      // No colour or label defined, bail out.
      if (!colour || !label) continue;

      // If the "country code" part looks like a sale type, then it has been omitted.
      if (
        !!countryCode &&
        ["vip", "sale"].includes(countryCode.toLowerCase())
      ) {
        saleType = countryCode;
        // @ts-expect-error
        countryCode = undefined;
      }

      // If this badge has a country code, but we are not in that country, bail out.
      if (
        !!countryCode &&
        countryCode.toLowerCase() !== this.countryCode.toLowerCase()
      ) {
        continue;
      }

      // If badge has a sale type but we are not on sale, bail out.
      if (
        !!saleType &&
        ["vip", "sale"].includes(saleType.toLowerCase()) &&
        !this.enableSale
      ) {
        continue;
      }

      // Set badge.
      badge = {
        children: label,
        variant: "light" === colour ? "secondary" : "primary",
      };

      // Prevent further iterations.
      break;
    }

    // Return badge if found.
    return badge;
  }
}
